import { ChangeEvent, useCallback, useEffect } from 'react';
import { CategoryTabedResponseDto } from '@tiendanube/common';
import { ProductRestrictionType } from '@tiendanube/common/src/enums';
import { Stack } from 'commons/components';
import { useModal } from 'commons/hooks';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import { useAutoResetSelection } from '../../../../ShippingRestrictionPages/hooks';
import SelectedItemsChipList from '../../../SelectedItemsChipList';
import { GenericSelectionModal } from '../../../SelectionModals';
import CategoryCheckboxItem from '../../../SelectionModals/CategoriesSelectionModal/CategoryCheckboxItem';

interface CategorySelectionSectionProps {
  categories: CategoryTabedResponseDto[];
  selectedCategories: number[];
  onCategoriesChange: (categories: number[]) => void;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

function CategorySelectionSection({
  categories,
  selectedCategories,
  onCategoriesChange,
  onChange,
}: Readonly<CategorySelectionSectionProps>): JSX.Element {
  const t = useTranslationShipping(
    'deliveryMethods.shippingLimiting.restrictions.restrictedProducts',
  );

  const [showModal, openModal, closeModal] = useModal();

  useEffect(() => {
    if (selectedCategories.length === 0) {
      openModal();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    handleSubmit: handleAutoResetSubmit,
    handleRemove: handleAutoResetRemove,
    handleClose: handleAutoResetClose,
  } = useAutoResetSelection({
    fieldName: 'productRestrictionType',
    resetValue: ProductRestrictionType.NO_RESTRICTION,
    onChange,
    onItemsChange: (items) =>
      onCategoriesChange(items.map((id) => Number.parseInt(id, 10))),
    selectedItems: selectedCategories.map((id) => id.toString()),
    allItems: categories,
    getItemId: (category) => category.id,
  });

  const handleSubmit = useCallback(
    (selectedCategoryIds: string[]) => {
      handleAutoResetSubmit(selectedCategoryIds);
      closeModal();
    },
    [handleAutoResetSubmit, closeModal],
  );

  const handleClose = useCallback(() => {
    handleAutoResetClose();
    closeModal();
  }, [handleAutoResetClose, closeModal]);

  const getCategoryName = useCallback(
    (id: string | number): string => {
      const category = categories.find((c) => c.id === id.toString());
      if (!category) return id.toString();

      return category.fixed
        ? `${category.fixed.prefix}${category.fixed.title}`
        : category.title;
    },
    [categories],
  );

  return (
    <>
      {selectedCategories.length > 0 && (
        <Stack.Item>
          <SelectedItemsChipList
            items={selectedCategories}
            editLinkText={t('editCategories')}
            getItemText={getCategoryName}
            onEdit={openModal}
            onRemove={handleAutoResetRemove}
          />
        </Stack.Item>
      )}

      {showModal && (
        <GenericSelectionModal<CategoryTabedResponseDto>
          isOpen
          onClose={handleClose}
          onSubmit={handleSubmit}
          preselected={(selectedCategories || []).map((id) => id.toString())}
          items={categories}
          getItemId={(category) => category.id}
          getItemSearchText={(category) => category.title}
          renderItem={(category, isSelected, onToggle) => (
            <CategoryCheckboxItem
              key={category.id}
              category={category}
              checked={isSelected}
              onChange={onToggle}
            />
          )}
          showSelectAll={false}
          translationPath="deliveryMethods.shippingLimiting.modals.categories"
        />
      )}
    </>
  );
}

export default CategorySelectionSection;
